import { CredentialType } from "@api/shared-runtime/enums";
import { LifeSupportKind } from "@api/shared-runtime/credential-vocabularies";
import type { Credential } from "../../../types";
import { asDetails, money } from "./detail-values";
import {
  healthScreeningTitle,
  stateCredentialTitle,
  stateLicenseTitle,
  summaryDateFact,
  summaryDateRange,
  summaryFacts,
  summaryIdentifier,
  summaryLabel,
  summaryLicenseType,
  summaryList,
  summaryState,
  summaryText,
  summaryTitle,
  type CredentialSummaryInput,
} from "./credential-summary-values";

export type CredentialRowSummary = {
  title: string;
  identifier?: string | null;
  secondary?: string[];
  meta?: string;
};

type SummaryBuilder = (
  input: CredentialSummaryInput,
) => Omit<CredentialRowSummary, "title"> & { title?: string | null };

function schedulesFact(value: unknown): string | null {
  const schedules = summaryList(value);
  return schedules ? `Schedules ${schedules}` : null;
}

/** Each type chooses its identifying facts. Expiration and provenance stay in
 * the status column, while full records remain available in the popover. */
const CREDENTIAL_SUMMARIES: Record<CredentialType, SummaryBuilder> = {
  [CredentialType.StateLicense]: (input) => ({
    title: stateLicenseTitle(input),
    identifier: summaryIdentifier("License", input.version.number),
    secondary: summaryFacts([
      summaryLicenseType(input.details.licenseType),
      input.details.compact === true
        ? "Interstate Medical Licensure Compact"
        : null,
      Array.isArray(input.details.imlcStateCodes)
        ? summaryList(input.details.imlcStateCodes.map(summaryState))
        : null,
    ]),
  }),
  [CredentialType.Dea]: ({ version, details }) => ({
    title: stateCredentialTitle(version.stateCode, "DEA Registration"),
    identifier: summaryIdentifier("DEA", version.number),
    secondary: summaryFacts([
      schedulesFact(details.schedules),
      details.businessActivity,
    ]),
  }),
  [CredentialType.Cds]: ({ version, details }) => ({
    title: stateCredentialTitle(version.stateCode, "CDS Registration"),
    identifier: summaryIdentifier("CDS", version.number),
    secondary: summaryFacts([schedulesFact(details.schedules)]),
  }),
  [CredentialType.BoardCertification]: ({ version, details }) => ({
    title: summaryTitle(details.specialty, "Board Certification"),
    identifier: summaryIdentifier("Certificate", version.number),
    secondary: summaryFacts([
      details.certifyingBoardName,
      summaryLabel(details.specialtyLevel),
    ]),
  }),
  [CredentialType.Ecfmg]: ({ version }) => ({
    title: "ECFMG Certification",
    identifier: summaryIdentifier("Certificate", version.number),
    secondary: summaryFacts([summaryDateFact("Issued", version.issuedAt)]),
  }),
  [CredentialType.Education]: ({ version, details }) => ({
    title:
      details.kind === "usmle"
        ? "USMLE Transcript"
        : summaryText(details.institutionName),
    secondary: summaryFacts([
      details.kind === "usmle" ? details.institutionName : details.degree,
      details.program,
      details.specialty,
      summaryDateRange({
        start: details.startDate,
        end: details.endDate ?? version.issuedAt,
      }),
    ]),
  }),
  [CredentialType.Training]: ({ version, details }) => ({
    title: summaryText(details.institutionName),
    secondary: summaryFacts([
      summaryLabel(details.trainingType),
      details.specialty,
      details.program,
      summaryDateRange({
        start: details.startDate,
        end: details.endDate ?? version.issuedAt,
      }),
    ]),
  }),
  [CredentialType.LifeSupport]: ({ version, details }) => ({
    title: summaryTitle(
      details.certKind,
      details.certKind === LifeSupportKind.Other ||
        !summaryText(details.certKind)
        ? "Life Support Certification"
        : "Certification",
    ),
    identifier: summaryIdentifier(
      "eCard",
      summaryText(details.eCardCode) ?? version.number,
    ),
    secondary: summaryFacts([
      details.issuingBody,
      summaryDateFact("Issued", version.issuedAt),
    ]),
  }),
  [CredentialType.HealthScreening]: ({ version, details }) => ({
    title: healthScreeningTitle(details.screeningKind),
    secondary: summaryFacts([
      details.declination === true ? "Declined" : summaryLabel(details.result),
      details.facility,
      summaryDateFact("Taken", version.issuedAt),
    ]),
  }),
  [CredentialType.BirthCertificate]: ({ version, details }) => ({
    title: "Birth Certificate",
    identifier: summaryIdentifier("Certificate", version.number),
    secondary: summaryFacts([
      details.issuingJurisdiction,
      details.nameOnDocument,
    ]),
  }),
  [CredentialType.Passport]: ({ version, details }) => ({
    title: summaryFacts([details.issuingCountry, "Passport"]).join(" "),
    identifier: summaryIdentifier("Passport", version.number),
    secondary: summaryFacts([details.nameOnDocument]),
  }),
  [CredentialType.DriversLicense]: ({ version, details }) => ({
    title: stateCredentialTitle(version.stateCode, "Driver’s License"),
    identifier: summaryIdentifier("License", version.number),
    secondary: summaryFacts([details.nameOnDocument]),
  }),
  [CredentialType.GovernmentId]: ({ version, details }) => ({
    title: summaryLabel(details.idKind),
    identifier: summaryIdentifier("ID", version.number),
    secondary: summaryFacts([details.issuer, details.nameOnDocument]),
  }),
  [CredentialType.MalpracticeInsurance]: ({ version, details }) => ({
    title: summaryText(details.carrierName),
    identifier: summaryIdentifier("Policy", version.number),
    secondary: summaryFacts([
      summaryLabel(details.coverageType),
      money(details.perOccurrenceLimit)
        ? `${money(details.perOccurrenceLimit)} per occurrence`
        : null,
      money(details.aggregateLimit)
        ? `${money(details.aggregateLimit)} aggregate`
        : null,
    ]),
  }),
  [CredentialType.MalpracticeClaimsHistory]: ({ details }) => {
    const claims = Array.isArray(details.claims) ? details.claims.length : 0;
    let disclosure: string | null = null;
    // An empty imported claim list is not a declaration that there were no claims.
    if (details.noClaims === true && claims === 0) {
      disclosure = "No claims disclosed";
    } else if (claims > 0) {
      disclosure = `${claims} claim${claims === 1 ? "" : "s"} disclosed`;
    }
    return {
      title: "Malpractice Claims History",
      secondary: summaryFacts([
        disclosure,
        summaryDateFact("Disclosed", details.disclosureDate),
      ]),
    };
  },
  [CredentialType.WorkHistory]: ({ details }) => ({
    title:
      details.kind === "gap"
        ? "Employment Gap"
        : summaryText(details.employerName),
    secondary: summaryFacts([
      details.kind === "gap" ? null : details.position,
      summaryDateRange({
        start: details.startDate,
        end: details.endDate,
        current: details.currentlyEmployed === true && details.kind !== "gap",
      }),
      summaryFacts([details.city, summaryState(details.state)]).join(", "),
    ]),
    meta:
      details.currentlyEmployed === true && details.kind !== "gap"
        ? "Current"
        : undefined,
  }),
  [CredentialType.HospitalAffiliation]: ({ details }) => ({
    title: summaryText(details.hospitalName),
    secondary: summaryFacts([
      summaryLabel(details.affiliationKind),
      details.department,
      summaryDateRange({ start: details.startDate, end: details.endDate }),
    ]),
    meta: details.primaryHospital === true ? "Primary" : undefined,
  }),
  [CredentialType.CaseLog]: ({ details }) => ({
    title: summaryText(details.facility),
    secondary: summaryFacts([
      typeof details.procedureCount === "number"
        ? `${details.procedureCount} procedures`
        : null,
      details.specialty,
      summaryDateRange({ start: details.periodStart, end: details.periodEnd }),
    ]),
  }),
  [CredentialType.Attestation]: ({ details }) => ({
    title: "Provider Attestation",
    secondary: summaryFacts([
      summaryLabel(details.method),
      summaryDateFact("Signed", details.signedAt),
    ]),
  }),
  [CredentialType.BackgroundCheck]: ({ details }) => ({
    title: summaryTitle(details.checkKind, "Background Check"),
    identifier: summaryIdentifier("Report", details.reportId),
    secondary: summaryFacts([
      details.vendor,
      summaryDateFact("Completed", details.completedAt),
    ]),
  }),
  [CredentialType.PeerReference]: ({ details }) => ({
    title: summaryText(details.refereeName),
    secondary: summaryFacts([
      details.refereeTitle,
      summaryLabel(details.relationship),
      details.refereeSpecialty,
      summaryDateFact("Completed", details.completedAt),
    ]),
  }),
  [CredentialType.CollaborativeAgreement]: ({ version, details }) => ({
    title: summaryTitle(
      summaryText(details.agreementKind) ?? "Collaborative",
      "Agreement",
    ),
    identifier: summaryIdentifier("Filing", version.number),
    secondary: summaryFacts([
      details.supervisingPhysicianName,
      summaryLabel(details.practiceSetting),
      summaryDateFact("Effective", version.issuedAt),
    ]),
  }),
  [CredentialType.Npi]: ({ version, details }) => ({
    title: "National Provider Identifier",
    identifier: summaryIdentifier("NPI", version.number),
    secondary: summaryFacts([
      summaryList(details.taxonomyCodes)
        ? `Taxonomy: ${summaryList(details.taxonomyCodes)}`
        : null,
      summaryDateFact("Enumerated", details.enumerationDate),
    ]),
  }),
  [CredentialType.MedicaidId]: ({ version }) => ({
    title: stateCredentialTitle(version.stateCode, "Medicaid Enrollment"),
    identifier: summaryIdentifier("Medicaid ID", version.number),
  }),
  [CredentialType.MedicareId]: ({ version, details }) => ({
    title: stateCredentialTitle(version.stateCode, "Medicare Enrollment"),
    identifier: summaryIdentifier(
      details.kind === "ptan" ? "PTAN" : "Medicare ID",
      version.number,
    ),
  }),
  [CredentialType.PersonalInfo]: ({ details }) => {
    const name = asDetails(details.name);
    const professional = asDetails(details.professional);
    return {
      title: [name.first, name.middle, name.last, name.suffix]
        .map(summaryText)
        .filter(Boolean)
        .join(" "),
      identifier: summaryIdentifier("CAQH ID", details.caqhId),
      secondary: summaryFacts([
        summaryLicenseType(professional.providerType),
        summaryState(professional.primaryPracticeState),
      ]),
    };
  },
  [CredentialType.Cv]: ({ version }) => ({
    secondary: summaryFacts([summaryDateFact("Dated", version.issuedAt)]),
  }),
  [CredentialType.Other]: ({ version }) => ({
    title: summaryText(version.number),
    secondary: summaryFacts([summaryDateFact("Issued", version.issuedAt)]),
  }),
};

/** A saved record always has a readable title, even when its details are sparse.
 * Missing records return null so their Add action remains the entry point. */
export function credentialRowSummary(
  credential: Credential,
): CredentialRowSummary | null {
  if (!credential.present) {
    return null;
  }
  const version = credential.version;
  const build =
    CREDENTIAL_SUMMARIES[credential.credentialType as CredentialType];
  const summary =
    version && build
      ? build({ version, details: asDetails(version.details) })
      : {};
  const document = credential.documents[0];
  return {
    ...summary,
    title:
      summaryText(summary.title) ??
      summaryText(credential.summary) ??
      summaryText(document?.title) ??
      summaryText(document?.fileName) ??
      credential.label,
    secondary: summaryFacts([
      ...(summary.secondary ?? []),
      summaryLabel(version?.status),
    ]),
  };
}
